import catalogData from '../data/catalog.json';
import {CatalogData, Catalog, Poet, Poem, getWiki} from '../types/poetry';

const data: CatalogData = catalogData;

// Serbian Cyrillic to Latin mapping
const cyrillicToLatin: { [key: string]: string } = {
    'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'ђ': 'dj', 'е': 'e',
    'ж': 'z', 'з': 'z', 'и': 'i', 'ј': 'j', 'к': 'k', 'л': 'l', 'љ': 'lj',
    'м': 'm', 'н': 'n', 'њ': 'nj', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's',
    'т': 't', 'ћ': 'c', 'у': 'u', 'ф': 'f', 'х': 'h', 'ц': 'c', 'ч': 'c',
    'џ': 'dz', 'ш': 's',
    'А': 'a', 'Б': 'b', 'В': 'v', 'Г': 'g', 'Д': 'd', 'Ђ': 'dj', 'Е': 'e',
    'Ж': 'z', 'З': 'z', 'И': 'i', 'Ј': 'j', 'К': 'k', 'Л': 'l', 'Љ': 'lj',
    'М': 'm', 'Н': 'n', 'Њ': 'nj', 'О': 'o', 'П': 'p', 'Р': 'r', 'С': 's',
    'Т': 't', 'Ћ': 'c', 'У': 'u', 'Ф': 'f', 'Х': 'h', 'Ц': 'c', 'Ч': 'c',
    'Џ': 'dz', 'Ш': 's'
};

// Latin special characters to basic Latin mapping
const latinToBasic: { [key: string]: string } = {
    'š': 's', 'Š': 's',
    'đ': 'dj', 'Đ': 'dj',
    'ć': 'c', 'Ć': 'c',
    'č': 'c', 'Č': 'c',
    'ž': 'z', 'Ž': 'z'
};

/**
 * Normalizes Serbian text for search comparison.
 * Converts Cyrillic to Latin and removes diacritics.
 * Example: "Змај" -> "zmaj", "Ћосић" -> "cosic", "Šantić" -> "santic"
 */
const normalizeForSearch = (text: string): string => {
    let result = '';
    for (const char of text) {
        if (cyrillicToLatin[char]) {
            result += cyrillicToLatin[char];
        } else if (latinToBasic[char]) {
            result += latinToBasic[char];
        } else {
            result += char;
        }
    }
    return result.toLowerCase();
};

export const getAllCatalogs = (): Catalog[] => {
    return data.catalogs;
};

export const getCatalogById = (catalogId: string): Catalog | undefined => {
    return data.catalogs.find(c => c.id === catalogId);
};

export const getPoetById = (poetId: string): { poet: Poet; catalog: Catalog } | undefined => {
    for (const catalog of data.catalogs) {
        const poet = catalog.poets.find(p => p.id === poetId);
        if (poet) {
            return {poet, catalog};
        }
    }
    return undefined;
};

// Helper to create a unique poem ID by combining poet and poem IDs
export const getFullPoemId = (poetId: string, poemId: string): string => {
    return `${poetId}|${poemId}`;
};

// Helper to parse a full poem ID back into poet and poem IDs
export const parseFullPoemId = (fullPoemId: string): { poetId: string; poemId: string } | undefined => {
    const separatorIndex = fullPoemId.indexOf('|');
    if (separatorIndex === -1) {
        return undefined;
    }
    return {
        poetId: fullPoemId.substring(0, separatorIndex),
        poemId: fullPoemId.substring(separatorIndex + 1)
    };
};

export const getPoemById = (fullPoemId: string): {
    poem: Poem;
    poet: Poet;
    catalog: Catalog;
    source?: string
} | undefined => {
    const parsed = parseFullPoemId(fullPoemId);
    if (!parsed) {
        return undefined;
    }

    const {poetId, poemId} = parsed;

    for (const catalog of data.catalogs) {
        const poet = catalog.poets.find(p => p.id === poetId);
        if (poet) {
            const poem = poet.poems.find(p => p.id === poemId);
            if (poem) {
                // Source precedence: poem > poet > catalog
                const source = poem.source || poet.source || catalog.source;
                return {poem, poet, catalog, source};
            }
        }
    }
    return undefined;
};

export const getAdjacentPoems = (fullPoemId: string): {
    prevPoem: { id: string; name: string } | null;
    nextPoem: { id: string; name: string } | null
} => {
    const parsed = parseFullPoemId(fullPoemId);
    if (!parsed) {
        return {prevPoem: null, nextPoem: null};
    }

    const {poetId, poemId} = parsed;

    for (const catalog of data.catalogs) {
        const poet = catalog.poets.find(p => p.id === poetId);
        if (poet) {
            const poemIndex = poet.poems.findIndex(p => p.id === poemId);
            if (poemIndex !== -1) {
                let prevPoem: { id: string; name: string } | null = null;

                if (poemIndex > 0) {
                    prevPoem = {id: getFullPoemId(poetId, poet.poems[poemIndex - 1].id), name: poet.poems[poemIndex - 1].name};
                } else if (getWiki(poet)) {
                    // First poem - link back to biography
                    prevPoem = {id: `bio:${poetId}`, name: 'О песнику'};
                }

                const nextPoem = poemIndex < poet.poems.length - 1
                    ? {id: getFullPoemId(poetId, poet.poems[poemIndex + 1].id), name: poet.poems[poemIndex + 1].name}
                    : null;
                return {prevPoem, nextPoem};
            }
        }
    }
    return {prevPoem: null, nextPoem: null};
};

export const fetchPoemContent = async (fullPoemId: string): Promise<string> => {
    const poemInfo = getPoemById(fullPoemId);
    if (!poemInfo) {
        throw new Error('Песма није пронађена');
    }
    const {catalog, poet, poem} = poemInfo;
    // Use the original poem.id for the file path
    const response = await fetch(`${process.env.PUBLIC_URL}/poems/${catalog.id}/${poet.id}/${poem.id}.txt`);
    if (!response.ok) {
        throw new Error('Песма није пронађена');
    }
    return response.text();
};

export interface SearchResult {
    catalogs: Catalog[];
    poets: { poet: Poet; catalog: Catalog }[];
    poems: { poem: Poem; poet: Poet; catalog: Catalog }[];
}

// --- Pre-built search index (normalized once at module load) ---
interface CatalogIndexEntry {
    normalizedName: string;
    catalog: Catalog;
}
interface PoetIndexEntry {
    normalizedName: string;
    poet: Poet;
    catalog: Catalog;
}
interface PoemIndexEntry {
    normalizedName: string;
    poem: Poem;
    poet: Poet;
    catalog: Catalog;
}

const catalogIndex: CatalogIndexEntry[] = [];
const poetIndex: PoetIndexEntry[] = [];
const poemIndex: PoemIndexEntry[] = [];

for (const catalog of data.catalogs) {
    catalogIndex.push({ normalizedName: normalizeForSearch(catalog.name), catalog });
    for (const poet of catalog.poets) {
        poetIndex.push({ normalizedName: normalizeForSearch(poet.name), poet, catalog });
        for (const poem of poet.poems) {
            poemIndex.push({ normalizedName: normalizeForSearch(poem.name), poem, poet, catalog });
        }
    }
}
// --------------------------------------------------------------

export const searchAll = (query: string): SearchResult => {
    const normalizedQuery = normalizeForSearch(query.trim());

    if (normalizedQuery.length < 2) {
        return {catalogs: [], poets: [], poems: []};
    }

    const catalogs = catalogIndex
        .filter(e => e.normalizedName.includes(normalizedQuery))
        .map(e => e.catalog);

    const poets = poetIndex
        .filter(e => e.normalizedName.includes(normalizedQuery))
        .map(({ poet, catalog }) => ({ poet, catalog }));

    const poems = poemIndex
        .filter(e => e.normalizedName.includes(normalizedQuery))
        .map(({ poem, poet, catalog }) => ({ poem, poet, catalog }));

    return {catalogs, poets, poems};
};

